import { Type, Static } from '@sinclair/typebox';

export const EmailSenderVerificationStatusSchema = Type.Union(
  [Type.Literal('verified'), Type.Literal('pending'), Type.Literal('unknown')],
  {
    description:
      'Readiness of the address for sending. ' +
      '`verified`: the address is confirmed and can be used in the `from` field. ' +
      '`pending`: the email sender exists but has not completed verification and is not ready to send (Gmail custom addresses). ' +
      '`unknown`: the provider does not expose per-address readiness (e.g. Outlook proxy addresses, whose usability depends on the tenant `SendFromAliasEnabled` setting).',
  },
);

export const EmailSenderSchema = Type.Object({
  object: Type.Literal('EmailSender', { description: 'Object type identifier, always "EmailSender".' }),
  email: Type.String({ description: 'Email address that can be placed in the `from` header when sending emails.' }),
  display_name: Type.Optional(
    Type.String({ description: 'Display name associated with the address, when the provider exposes one.' }),
  ),
  is_primary: Type.Boolean({
    description: 'Whether this is the primary address of the mailbox (the address used to authenticate the account).',
  }),
  is_default: Type.Optional(
    Type.Boolean({
      description:
        'Whether this is the default "From:" address used when composing a new message. Only exposed by providers that have a distinct default sender (Gmail).',
    }),
  ),
  verification_status: EmailSenderVerificationStatusSchema,
  signature: Type.Optional(
    Type.String({
      description:
        'HTML signature configured for this address, when the provider exposes one (Gmail). ' +
        'It is not appended automatically when sending: include it yourself at the end of the `body`/`html` of the email. ' +
        'Absent when the address has no signature, and not supported by Outlook.',
    }),
  ),
});

export type EmailSenderVerificationStatus = Static<typeof EmailSenderVerificationStatusSchema>;
export type EmailSender = Static<typeof EmailSenderSchema>;
